<script setup lang="ts">
import StackLayout from '@/components/Layouts/StackLayout.vue'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import store from '@/store'
import { ECheckpointActions } from '@/store/checkpointStore/CheckpointStoreTypes'
import { EStoreModules } from '@/store/storeType'
import type { CheckpointModel } from '@/types/models/CheckpointModel'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import { EPOIPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import type { SocketCheckpointDetectionModel } from '@/types/socket/SocketDetectionModel'
import CheckpointDetailView from '@/views/CheckpointsView/CheckpointDetailView.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import CheckpointCardMode from './CheckpointsTable/CheckpointCardMode.vue'
import EventCreateCheckpoint from './CreateCheckpoint/EventCreateCheckpoint.vue'

type Props = {
    currentTabIndex: number
    partnerConfig?: PartnerModel
    detections: SocketCheckpointDetectionModel[]
}
const props = defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
    (e: 'goTo', value?: CheckpointModel): void
    (e: 'checkpointUpdate', value?: SocketCheckpointDetectionModel[]): void
    (e: 'refreshDevices', associates: number[]): void
}
const emit = defineEmits<Emits>()

const route = useRoute()
const siteId = computed(() => parseInt(route.params.id.toString()))
const checkpoint = ref<CheckpointModel>()

const data = computed(() => store.state.checkpoint)
const latestDetections = ref<SocketCheckpointDetectionModel[]>([])
watch(
    () => props.detections,
    newDetection => (latestDetections.value = newDetection)
)

const getCheckpoints = () =>
    store
        .dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.FETCH_CHECKPOINTS}`, {
            id: siteId.value,
            params: { per_page: -1 }
        })
        .then((res: CheckpointModel[]) => {
            latestDetections.value = res.map(item => ({
                poi_id: item.id,
                detections: []
            }))
            if (checkpoint.value)
                checkpoint.value = res.find(item => item.id === checkpoint.value?.id)
        })

onMounted(() => {
    getCheckpoints()
})
const handleDeleteCheckpoint = (value: CheckpointModel) =>
    store
        .dispatch(`${EStoreModules.CHECKPOINT}/${ECheckpointActions.DELETE_CHECKPOINT}`, {
            siteId: siteId.value,
            poiId: value.id,
            name: value.name
        })
        .then(() => getCheckpoints())

const handleAssociateDevice = () => {
    getCheckpoints()
    emit(
        'refreshDevices',
        data.value.data.checkpoints.flatMap(
            item => item.associates?.flatMap(associatedDevice => associatedDevice.device_id) ?? []
        ) ?? []
    )
}
</script>

<template>
    <StackLayout direction="column" :gap="8">
        <StackLayout direction="column" :gap="8" class="full-width flex-wrap">
            <PermissionWrapper :permission="EPOIPerm.CHECKPOINTS_EDIT">
                <EventCreateCheckpoint
                    :siteId="siteId"
                    :partnerConfig="partnerConfig"
                    :loading="!!store.state.checkpoint.isLoading"
                    @refresh="getCheckpoints" />
            </PermissionWrapper>
            <CheckpointCardMode
                :currentTabIndex="currentTabIndex"
                :data="data.data.checkpoints"
                :detections="latestDetections"
                :loading="data.isLoading ?? false"
                :siteId="siteId"
                :editPermission="EPOIPerm.CHECKPOINTS_EDIT"
                :deletePermission="EPOIPerm.CHECKPOINTS_DELETE"
                @goTo="checkpoint = $event"
                @delete="handleDeleteCheckpoint"
                @refresh="getCheckpoints" />
        </StackLayout>
        <CheckpointDetailView
            v-if="checkpoint"
            :checkpoint="checkpoint"
            :partnerConfig="partnerConfig"
            @refresh="getCheckpoints"
            @refreshAssociate="handleAssociateDevice" />
    </StackLayout>
</template>
